import Faq, {QA, SectionDivider} from "@components/Faq/Faq";
import Layout from "@components/Layout";
import ApptReasonFaq from "@components/Reason/ApptReasonFaq";
import CareDiscoverySearch from "@components/Reason/CareDiscoverySearch/CareDiscoverySearch";
import Insurances, {getCareInsuranceText} from "@components/Reason/Insurances";
import {Wrapper} from "@page-containers/get-care/components/Comp";
import {RelatedLinks} from "@page-containers/get-care/components/RelatedLinks";
import {loadAppointmentReasons} from "@services/monolith/appointmentReasons";
import {NextPage} from "next";
import {useTranslation} from "ni18n";
import React, {useEffect, useMemo, useState} from "react";
import {PracticeId} from "src/constants/practiceIds";
import {actions} from "src/store";
import {NextPageContextWithStore} from "src/store/types";
import {CareInsuranceType, CareTopic} from "src/utils/cms/types";
import {toTitleCase} from "src/utils/titleCase";

import {getOgImagePath} from "../../../opengraph/utils/encodeUrl";
import {NavTheme} from "../../components/v5/Nav/useNavState";
import {useSendDefaultResultsViewedEvent} from "../../components/v5/Reason/CareDiscoverySearch/careDiscoveryAnalytics";
import {getCareDiscoveryColorScheme} from "../../constants/Reason/getCareDiscoveryColorScheme";
import useMediaQuery from "../../useMediaQuery";
import {ExternalLinks} from "@page-containers/get-care/components/ExternalLinks";

export type Props = {
  content: CareTopic;
};

export const Topic: NextPage<Props> = ({content}) => {
  const i18n = useTranslation();
  const isDetectTopicPage = content.slug === "covid-care-for-detect-patients";
  const [detectHeaderImages, setDetectHeaderImages] = useState(undefined);
  const detectHeaderImageList = useMemo(
    () => [
      {
        src: "/partners/detect/detect-hub",
        alt: i18n.t("Parts of Detect Hub"),
      },
      {
        src: "/partners/detect/starter-kit",
        alt: i18n.t("Boxes of Detect Hub"),
      },
      {
        src: "/partners/detect/starter-kit-try",
        alt: i18n.t("The patient takes the swab using the kit"),
      },
    ],
    [i18n],
  );

  // there is an issue that Sticky doesnt get sized correctly if ImageCarousel is loaded upfront. Until the issue is fixed,
  // we delay rendering of the ImageCarousel here.
  useEffect(() => {
    if (isDetectTopicPage) {
      // @ts-expect-error TS2345: Argument of type '{ src: string; alt: string; }[]' is not assignable to parameter of type 'SetStateAction<undefined>'.
      const timer = setTimeout(() => setDetectHeaderImages(detectHeaderImageList), 500);
      return () => clearTimeout(timer);
    }
  }, [i18n]);

  const {backgroundColor, patternSrc} = getCareDiscoveryColorScheme(content.id, content.name);

  const isSm = useMediaQuery("sm");

  const generalFaqs = useMemo(
    () =>
      content.shared_faqs?.reduce<(QA | SectionDivider)[]>(
        (acc, next) =>
          next.faqs ? [...acc, ...next.faqs.map(qa => ({q: qa.question, a: qa.answer}))] : acc,
        [],
      ) || [],
    [content.shared_faqs],
  );

  const ogImagePath = getOgImagePath("/get-care/[slug]", {
    id: content.id,
    title: content.name,
  });

  const pageTitle = content.meta?.title || `${toTitleCase(content.name)} | Carbon Health`;
  const defaultResults = useMemo(
    () => [...(content.cares || []), ...(content.related_topics || [])],
    [content.id],
  );

  const viewId = useSendDefaultResultsViewedEvent({
    results: defaultResults,
    isLoading: false,
  });

  return (
    <Layout
      key={JSON.stringify(isSm)} // Forces nav to render with isSm when not SSR
      pageTitle={pageTitle}
      pageDescription={content.meta?.description || content.description}
      navTheme={isSm ? NavTheme.BLACK : NavTheme.DEFAULT}
      navStartTransparent={isSm}
      absolutePageImage={ogImagePath}
      wrapClasses="ovf-unset" // required for position sticky to work
    >
      <Wrapper.Grid>
        <Wrapper.Faq backgroundColor={backgroundColor} patternSrc={patternSrc}>
          <ApptReasonFaq
            title={content.name}
            description={content.description}
            faq={content.faqs?.map(qa => ({q: qa.question, a: qa.answer})) || []}
            isInsuranceAccepted={content.insurance_type !== CareInsuranceType.NoInsurance}
          />
        </Wrapper.Faq>

        <Wrapper.Action isSticky={!isSm}>
          {content.external_links ? (
            <ExternalLinks externalLinks={content.external_links} />
          ) : (
            <CareDiscoverySearch
              defaultResults={defaultResults}
              dialogTitle={i18n.t("What brings you in?")}
              isSearchEnabled={
                !isDetectTopicPage &&
                content.insurance_type !== CareInsuranceType.OccupationalHealth
              }
              headerImages={isDetectTopicPage ? detectHeaderImages : undefined}
              hideHeadline={isSm}
              viewId={viewId}
            />
          )}
        </Wrapper.Action>

        <Wrapper.Insurances>
          <Insurances
            headerText={getCareInsuranceText(
              i18n.t.bind(i18n),
              content.insurance_type,
              content.name,
            )}
            insuranceType={content.insurance_type}
            isFullWidth={false}
          />
        </Wrapper.Insurances>

        <Wrapper.GlobalFaq>
          <Faq
            className="ph0-f ph0-md-f pt0-sm"
            faq={generalFaqs}
            supportClickEventData={{extraData: {apptReasonId: content.id}}}
            skipLinkTargetId="footer"
          />
        </Wrapper.GlobalFaq>
      </Wrapper.Grid>
      <RelatedLinks condition={content.name} relatedTo={content.related_topics} />
    </Layout>
  );
};

export const getInitialProps = async ({
  content,
  store,
}: NextPageContextWithStore & {content: CareTopic}) => {
  if (!store.getState().config.reasons) {
    const reasons = ((await loadAppointmentReasons(PracticeId.CARBON)) || [])
      .mapPick([
        "id",
        "slug",
        "name",
        "iconName",
        "description",
        "specialtyIds",
        "newPatientDuration",
        "supportsVideoVisit",
        "supportsInstantVisit",
        "practiceApptReasonSpecialties",
      ])
      .filter(r =>
        r.practiceApptReasonSpecialties.some(reasonSpecialty => reasonSpecialty.sortIndex),
      );

    store.dispatch(
      actions.setConfig({
        reasons,
      }),
    );
  }

  return {content};
};
